import { memo, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  ButtonComponent,
  PinInputComponent,
  PinRulesComponent,
} from "@zenith/components";
import { GreenCheck } from "@zenith/assets/illustrations/GreenCheck";
import GreenCheckCircle from "@zenith/assets/icons/components/GreenCheckCircle";
import Info from "@zenith/assets/icons/Info";
import {
  ACCESS,
  DEFAULT_CHANNEL,
  PIN_LENGTH,
  TRANSACTIONS_PIN_LENGTH,
} from "@zenith/data/constants";
import { DASHBOARD, SIGN_IN } from "@zenith/routes/paths";
import {
  TeamManagementControllerService,
  UtilityApiService,
  type InvitationDetails,
} from "@zenith/services/user";
import { BusinessProfileControllerService } from "@zenith/services/user/services/BusinessProfileControllerService";
import { encrypt } from "@zenith/utils";

const X_API_KEY = import.meta.env.VITE_X_API_KEY;

const PASSCODE_LENGTH = PIN_LENGTH;
const TRANSACTION_PIN_LENGTH = TRANSACTIONS_PIN_LENGTH;

const emptyDigits = (length: number): Array<number | string> =>
  new Array(length).fill("");

type Digits = Array<number | string>;

const joinDigits = (digits: Digits) => digits.join("");

const setDigitAt =
  (setter: (value: Digits) => void, current: Digits) =>
  (entry: number | string, index: number) => {
    const next = [...current];
    next[index] = entry;
    setter(next);
  };

const completeCode = (value: string, length: number) =>
  value.length === length && new RegExp(`^\\d{${length}}$`).test(value);

// Design (node 73228:168554, "Create New Passcode") checks sequential/repeated
// against the passcode alone, independently, and marks each rule as
// satisfied the moment the passcode is complete and doesn't break it - the
// transaction PIN carries no such rule.
const isRepeatedOf = (value: string, length: number) =>
  completeCode(value, length) && /^(\d)\1+$/.test(value);

const isSequentialOf = (value: string, length: number) => {
  if (!completeCode(value, length)) return false;
  const digits = value.split("").map(Number);
  const step = digits[1] - digits[0];
  return digits.every(
    (digit, index) => index === 0 || digit - digits[index - 1] === step
  );
};

const isRepeated = (value: string) => isRepeatedOf(value, PASSCODE_LENGTH);

const isSequential = (value: string) => isSequentialOf(value, PASSCODE_LENGTH);

const passcodeProblem = (value: string) => {
  if (isRepeated(value))
    return "Your passcode cannot be repeated numbers only.";
  if (isSequential(value))
    return "Your passcode cannot be sequential numbers only.";
  return "";
};

type Screen =
  | "loading"
  | "passcode"
  | "transactionPin"
  | "signInRequired"
  | "error"
  | "done";

/**
 * Put the session into the business the invitation was for.
 *
 * Accepting only creates the membership: the token the invitee signed in with
 * still points at whatever profile they used - for someone who runs their own
 * business that is their own merchant profile. Without this, "You have been
 * added to X" is followed by the dashboard of a different business, and the
 * role they were invited for (cashier, accountant...) never takes effect.
 *
 * Best effort on purpose: the invitation is already accepted by the time this
 * runs, so a failure must not turn a successful join into an error screen.
 *
 * A switch is only permitted on a session that was just minted - the backend
 * answers ZENZ-USER-134 ("Sign in again to select another business") for a
 * token that is already tied to a business - so the caller has to be told
 * whether it worked, and ask the invitee to sign in when it did not.
 */
const switchToJoinedProfile = async (
  businessName: string | undefined,
  role: string | undefined
): Promise<boolean> => {
  if (!businessName) return false;
  try {
    const response = await BusinessProfileControllerService.profiles();
    const profiles = response?.data ?? [];
    const joined =
      profiles.find(
        (profile) =>
          profile.businessName === businessName &&
          (!role || profile.role === role)
      ) ?? profiles.find((profile) => profile.businessName === businessName);
    if (!joined?.businessId) return false;
    await BusinessProfileControllerService.select({
      businessId: joined.businessId,
    });
    return true;
  } catch {
    // Leave the session as it was; the membership itself is in place.
    return false;
  }
};

/**
 * Where an invited team member completes onboarding from the emailed link.
 *
 * No phone step and no OTP: the emailed token is itself the proof of
 * identity (it resolves straight to the invitee's details), so the flow
 * goes directly to create passcode -> create transaction PIN -> success
 * once the token checks out. The token is never built or cached here, and
 * resending an invitation makes the previous link stop working. Someone who
 * already has a Finopay login signs in and then accepts the membership
 * instead of setting a passcode.
 */
const AcceptInvitationPage = memo(function AcceptInvitationPage() {
  const { token = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [screen, setScreen] = useState<Screen>("loading");
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [passcodeDigits, setPasscodeDigits] = useState<Digits>(() =>
    emptyDigits(PASSCODE_LENGTH)
  );
  const [confirmPasscodeDigits, setConfirmPasscodeDigits] = useState<Digits>(
    () => emptyDigits(PASSCODE_LENGTH)
  );
  const [pinDigits, setPinDigits] = useState<Digits>(() =>
    emptyDigits(TRANSACTION_PIN_LENGTH)
  );
  const [confirmPinDigits, setConfirmPinDigits] = useState<Digits>(() =>
    emptyDigits(TRANSACTION_PIN_LENGTH)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  // True when the membership is in place but the session could not be moved
  // into the joined business, which is the normal case for someone who was
  // already signed in: the backend only allows a switch on a fresh session.
  const [needsFreshSignIn, setNeedsFreshSignIn] = useState(false);

  const passcode = joinDigits(passcodeDigits);
  const confirmPasscode = joinDigits(confirmPasscodeDigits);
  const pin = joinDigits(pinDigits);
  const confirmPin = joinDigits(confirmPinDigits);

  useEffect(() => {
    let isCurrent = true;
    if (!token) {
      setErrorMessage("This invitation link is incomplete.");
      setScreen("error");
      return;
    }
    TeamManagementControllerService.resolve(token)
      .then((response) => {
        if (!isCurrent) return;
        const details = response?.data ?? null;
        if (!details) {
          setErrorMessage("This invitation link is no longer valid.");
          setScreen("error");
          return;
        }
        setInvitation(details);
        if (details.requiresOnboarding) {
          setScreen("passcode");
          return;
        }
        // Already has a Finopay login: accept as the signed-in invitee. This
        // page is outside TokenHOC, so the Axios interceptor that normally
        // attaches `Authorization` is not mounted - the token has to be passed
        // explicitly or the call answers 401 even for a signed-in user.
        TeamManagementControllerService.acceptExisting(
          { token },
          sessionStorage.getItem(ACCESS) ?? undefined
        )
          .then(async () => {
            if (!isCurrent) return;
            const switched = await switchToJoinedProfile(
              details.businessName,
              details.role
            );
            if (!isCurrent) return;
            setNeedsFreshSignIn(!switched);
            setScreen("done");
          })
          .catch(() => isCurrent && setScreen("signInRequired"));
      })
      .catch((error) => {
        if (!isCurrent) return;
        // A dead link and a broken server are different stories; saying
        // "expired" for a 5xx sends the invitee chasing a link that is fine.
        const status = (error as { status?: number })?.status ?? 0;
        setErrorMessage(
          status === 404 || status === 410 || status === 400
            ? "This invitation link has expired or has already been used."
            : status >= 500
            ? "We couldn't load your invitation. Please try again in a moment."
            : error?.body?.message ?? "We couldn't load your invitation."
        );
        setScreen("error");
      });
    return () => {
      isCurrent = false;
    };
  }, [token]);

  const passcodeStepProblem =
    passcodeProblem(passcode) ||
    (confirmPasscode && passcode !== confirmPasscode
      ? "The passcodes do not match."
      : "");

  const canSubmitPasscode =
    completeCode(passcode, PASSCODE_LENGTH) &&
    passcode === confirmPasscode &&
    !passcodeStepProblem;

  const pinStepProblem =
    confirmPin && pin !== confirmPin ? "The PINs do not match." : "";

  const canSubmitPin =
    completeCode(pin, TRANSACTION_PIN_LENGTH) &&
    pin === confirmPin &&
    !pinStepProblem &&
    !isSubmitting;

  const submit = () => {
    if (!canSubmitPin) return;
    setIsSubmitting(true);
    setErrorMessage("");
    // Credentials never leave the browser in the clear: every other PIN and
    // passcode in this app is RSA-encrypted with the server's public key
    // before it is sent, and this endpoint takes the same values.
    UtilityApiService.getPublicKey(DEFAULT_CHANNEL, X_API_KEY)
      .then((response) => {
        const publicKeyBase64 = response?.data?.publicKeyBase64 ?? "";
        if (!publicKeyBase64) {
          throw new Error("missing public key");
        }
        const encryptedPasscode = encrypt(passcode, publicKeyBase64);
        const encryptedConfirmPasscode = encrypt(
          confirmPasscode,
          publicKeyBase64
        );
        const encryptedPin = encrypt(pin, publicKeyBase64);
        const encryptedConfirmPin = encrypt(confirmPin, publicKeyBase64);
        if (
          !encryptedPasscode ||
          !encryptedConfirmPasscode ||
          !encryptedPin ||
          !encryptedConfirmPin
        ) {
          throw new Error("encryption failed");
        }
        return TeamManagementControllerService.accept({
          token,
          passcode: encryptedPasscode,
          confirmPasscode: encryptedConfirmPasscode,
          pin: encryptedPin,
          confirmPin: encryptedConfirmPin,
        });
      })
      .then(async () => {
        const switched = await switchToJoinedProfile(
          invitation?.businessName,
          invitation?.role
        );
        setNeedsFreshSignIn(!switched);
        setScreen("done");
      })
      .catch((error) =>
        setErrorMessage(
          error?.body?.message ??
            (error?.message === "missing public key" ||
            error?.message === "encryption failed"
              ? "We couldn't secure your details right now. Please try again."
              : "We couldn't complete your invitation.")
        )
      )
      .finally(() => setIsSubmitting(false));
  };

  // This page renders inside AuthenticationLayout's <Outlet />, which already
  // supplies the promo panel and centers its content column full-height - no
  // min-h-screen wrapper needed here (that produced a second, conflicting
  // full-height column nested inside the layout's own).
  const shell = (children: React.ReactNode) => (
    <div className="mx-auto flex w-full max-w-md flex-col gap-6 px-6 py-10">
      {children}
    </div>
  );

  if (screen === "loading") {
    return shell(
      <p className="text-center text-sm text-neutral-600">
        Loading your invitation...
      </p>
    );
  }

  if (screen === "error") {
    return shell(
      <>
        <h1 className="text-center text-base font-semibold text-neutral-900">
          Invitation unavailable
        </h1>
        <p className="text-center text-sm text-neutral-600">{errorMessage}</p>
        <ButtonComponent
          id="accept-invitation-back"
          type="primary"
          size="large"
          label="Go to sign in"
          fullWidth
          onClick={() => navigate(SIGN_IN, { replace: true })}
        />
      </>
    );
  }

  if (screen === "signInRequired") {
    return shell(
      <>
        <h1 className="text-center text-base font-semibold text-neutral-900">
          Sign in to accept
        </h1>
        <p className="text-center text-sm text-neutral-600">
          You already have a Finopay account
          {invitation?.email ? ` (${invitation.email})` : ""}. Sign in as that
          user to join {invitation?.businessName ?? "this business"}.
        </p>
        <ButtonComponent
          id="accept-invitation-sign-in"
          type="primary"
          size="large"
          label="Sign in"
          fullWidth
          onClick={() =>
            navigate(SIGN_IN, {
              state: { returnTo: `/accept-invitation/${token}` },
            })
          }
        />
      </>
    );
  }

  if (screen === "done") {
    // Matches node 73228:187292 exactly: tick-circle illustration, "Your
    // profile has been created", a single "Proceed" button. The one addition
    // is the case where the session could not be moved into the joined
    // business: going straight to the dashboard would show a different
    // business than the one just joined, so the button asks for the sign-in
    // the backend requires instead.
    const joinedBusiness = invitation?.businessName ?? "the business";
    return shell(
      <div className="my-auto flex h-auto w-full flex-col items-center justify-center rounded-2.5xl bg-white pb-10 text-center shadow-lg">
        <GreenCheck height={220} width={450} />
        <div className="w-11/12 space-y-6 md:w-2/3">
          <p className="text-3.5xl font-bold text-neutral-900">
            Your profile has been created
          </p>
          <p className="text-sm text-neutral-500">
            You have been added to {joinedBusiness}.
          </p>
          {needsFreshSignIn && (
            <p className="text-sm text-neutral-600">
              Sign in again to open {joinedBusiness}
              {invitation?.email ? ` as ${invitation.email}` : ""}.
            </p>
          )}
          <ButtonComponent
            id="accept-invitation-proceed"
            type="primary"
            size="large"
            label={needsFreshSignIn ? "Sign in to continue" : "Proceed"}
            fullWidth
            onClick={() =>
              needsFreshSignIn
                ? navigate(SIGN_IN, {
                    replace: true,
                    state: { returnTo: DASHBOARD },
                  })
                : navigate(DASHBOARD, { replace: true })
            }
          />
        </div>
      </div>
    );
  }

  if (screen === "passcode") {
    // Design (node 73228:168554): each rule turns into a green check once
    // the passcode is complete and doesn't break it - not tied to the
    // confirm field.
    const passcodeComplete = completeCode(passcode, PASSCODE_LENGTH);
    const rules = [
      {
        key: "sequential",
        text: "Sequential numbers only (example: 1234; 5678; 9876)",
        satisfied: passcodeComplete && !isSequential(passcode),
      },
      {
        key: "repeated",
        text: "Repeated numbers only (example: 1111; 4444; 0000)",
        satisfied: passcodeComplete && !isRepeated(passcode),
      },
    ];

    return shell(
      <>
        <div className="flex items-start">
          <ButtonComponent
            id="accept-invitation-passcode-back"
            label="Back"
            type="tertiary"
            size="large"
            // No earlier in-flow step to return to anymore - this leaves
            // the invitation rather than resuming it.
            onClick={() => navigate(SIGN_IN, { replace: true })}
          />
        </div>

        <div className="text-center">
          <p className="mb-2 text-sm text-neutral-600">
            {invitation?.businessName}
          </p>
          <h1 className="text-2xl font-medium text-neutral-900">
            Create New Passcode
          </h1>
          <p className="mt-2 text-base text-neutral-600">
            This PIN is only used to login into your account
          </p>
        </div>

        <PinInputComponent
          id="accept-invitation-passcode"
          label="Create six (6) digit PIN"
          labelClassName=""
          pin={passcodeDigits}
          onPinChanged={setDigitAt(setPasscodeDigits, passcodeDigits)}
          pinLength={PASSCODE_LENGTH}
          pinContainerClass="gap-2"
          inputClassName="w-11"
          masked
          onBlur={() => undefined}
        />
        <PinInputComponent
          id="accept-invitation-confirm-passcode"
          label="Confirm six (6) digit PIN"
          labelClassName=""
          pin={confirmPasscodeDigits}
          onPinChanged={setDigitAt(
            setConfirmPasscodeDigits,
            confirmPasscodeDigits
          )}
          pinLength={PASSCODE_LENGTH}
          pinContainerClass="gap-2"
          inputClassName="w-11"
          masked
          onBlur={() => undefined}
        />

        <div className="text-xs text-neutral-600">
          <p className="mb-2">
            To protect your account, your Passcode must not have:
          </p>
          <ul className="flex flex-col gap-2">
            {rules.map((rule) => (
              <li key={rule.key} className="flex items-center gap-2">
                {rule.satisfied ? (
                  <GreenCheckCircle width={16} height={16} />
                ) : (
                  <Info width={16} height={16} stroke="#9B9B9B" />
                )}
                <span className={rule.satisfied ? "text-accent-success" : ""}>
                  {rule.text}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {passcodeStepProblem && (
          <p className="text-center text-sm text-red-500">
            {passcodeStepProblem}
          </p>
        )}

        <ButtonComponent
          id="accept-invitation-passcode-continue"
          type="primary"
          size="large"
          label="Continue"
          fullWidth
          disabled={!canSubmitPasscode}
          onClick={() => setScreen("transactionPin")}
        />
      </>
    );
  }

  // screen === "transactionPin". Not part of node 73105:17398's own frames,
  // but POST /api/corporate/team/invitations/accept still requires pin and
  // confirmPin alongside the passcode - same copy as SignUpTransactionsPinPage
  // for consistency with every other "create a transaction PIN" screen.
  return shell(
    <>
      <div className="flex items-start">
        <ButtonComponent
          id="accept-invitation-pin-back"
          label="Back"
          type="tertiary"
          size="large"
          onClick={() => setScreen("passcode")}
        />
      </div>

      <div className="text-center">
        <h1 className="text-2xl font-medium text-neutral-900">
          Transaction PIN
        </h1>
        <p className="mt-2 text-base text-neutral-600">
          This will be used to authorise your transactions
        </p>
      </div>

      <PinInputComponent
        id="accept-invitation-pin"
        label="Create four (4) digit PIN"
        labelClassName=""
        pin={pinDigits}
        onPinChanged={setDigitAt(setPinDigits, pinDigits)}
        pinLength={TRANSACTION_PIN_LENGTH}
        pinContainerClass="gap-2"
        inputClassName="w-11"
        masked
        onBlur={() => undefined}
      />
      <PinInputComponent
        id="accept-invitation-confirm-pin"
        label="Confirm four (4) digit PIN"
        labelClassName=""
        pin={confirmPinDigits}
        onPinChanged={setDigitAt(setConfirmPinDigits, confirmPinDigits)}
        pinLength={TRANSACTION_PIN_LENGTH}
        pinContainerClass="gap-2"
        inputClassName="w-11"
        masked
        onBlur={() => undefined}
      />

      {/* The same rules panel the sign-up flow shows under its PIN fields: the
          rules react to what has been typed, so a broken rule is named. */}
      <PinRulesComponent
        className="text-xs text-neutral-600"
        isRepeated={isRepeatedOf(pin, TRANSACTION_PIN_LENGTH)}
        isSequential={isSequentialOf(pin, TRANSACTION_PIN_LENGTH)}
        necessaryPinLength={TRANSACTION_PIN_LENGTH}
        pinLength={pin.length}
      />

      {(pinStepProblem || errorMessage) && (
        <p className="text-center text-sm text-red-500">
          {pinStepProblem || errorMessage}
        </p>
      )}

      <ButtonComponent
        id="accept-invitation-submit"
        type="primary"
        size="large"
        label="Continue"
        fullWidth
        disabled={!canSubmitPin}
        isLoading={isSubmitting}
        onClick={submit}
      />
    </>
  );
});

export default AcceptInvitationPage;
